import styles from './styles.module.scss';
import { FeeProps } from './props';
import { ErrorChip } from '@starsoft/common/components';
import FeeLoader from './Loader';
import { useFormContext } from 'react-hook-form';
import FeeSkeleton from './Skeleton';
import { useTranslation, useWithdrawForm } from '../../../../../../../../hooks';
import { WithdrawFormCreateDto } from '../../../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import { WalletService } from '@starsoft/common/services';

/**
 * Fee Component
 * This component is responsible for displaying the fee information in the withdrawal form.
 * It handles loading states, errors, and displays the fee and network fee if applicable.
 *
 * @param {FeeProps} props - The properties passed to the component.
 * @param {boolean} props.noChain - A flag indicating whether to display the network fee.
 * @param {boolean} props.isLoading - A flag indicating whether the fee data is loading.
 * @returns {JSX.Element} The rendered fee component, which includes fee and network fee information.
 *
 * @example
 * // Usage example:
 * <Fee noChain={false} isLoading={true} />
 */
export default function Fee({ noChain, isLoading }: FeeProps) {
  // Retrieve the translation function for the 'withdraw' namespace
  const { t } = useTranslation('withdraw');
  // Retrieve form context methods for watching and setting form values
  const { watch, setValue } = useFormContext<WithdrawFormCreateDto>();
  // Use custom hook to manage withdrawal form state and methods
  const {
    feeQuery: { fee, isErrorFee, isLoadingFee, refetchFee },
  } = useWithdrawForm(watch, setValue);

  // If there is an error fetching the fee, render an error chip
  if (isErrorFee) {
    return <ErrorChip action={refetchFee} cardBg />;
  }

  // If the fee data is still loading, render a skeleton loader
  if (isLoading) {
    return <FeeSkeleton noChain={noChain} />;
  }

  // Render the fee and network fee information
  return (
    <div className={styles.container}>
      <div className={styles.container__row}>
        <p className={styles.container__label}>{t('fee_label')}</p>

        <p className={styles.container__value}>
          <FeeLoader isLoading={isLoadingFee}>
            {WalletService.maskCurrency({
              amount: getMoneyOrAmount(fee?.fee),
            })}
          </FeeLoader>
        </p>
      </div>
      {!noChain && (
        <div className={styles.container__row}>
          <p className={styles.container__label}>{t('network_fee_label')}</p>

          <p className={styles.container__value}>
            <FeeLoader isLoading={isLoadingFee}>
              {WalletService.maskCurrency({
                amount: getMoneyOrAmount(fee?.chainFee),
              })}
            </FeeLoader>
          </p>
        </div>
      )}
    </div>
  );
}
